import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { PageHero, Section } from "@/components/site/section";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";

export const Route = createFileRoute("/log-in")({
  head: () => ({
    meta: [
      { title: "Log In | Argentinian.Soccer" },
      { name: "description", content: "Log in to Argentinian.Soccer to vote in polls and post in the community." },
      { property: "og:title", content: "Log In | Argentinian.Soccer" },
      { property: "og:url", content: "/log-in" },
    ],
    links: [{ rel: "canonical", href: "/log-in" }],
  }),
  component: LogInPage,
});

function LogInPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/" });
    });
  }, [navigate]);

  async function handleEmail(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "");
    const password = String(fd.get("password") ?? "");
    setLoading(true);
    if (mode === "signup") {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin },
      });
      setLoading(false);
      if (error) return toast.error(error.message);
      toast.success("Check your inbox to confirm your account.");
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setLoading(false);
      if (error) return toast.error(error.message);
      toast.success("Welcome back.");
      navigate({ to: "/" });
    }
  }

  async function handleGoogle() {
    const res = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
    if (res.error) toast.error(res.error.message ?? "Google sign-in failed.");
    if (!res.redirected && !res.error) navigate({ to: "/" });
  }

  return (
    <>
      <PageHero eyebrow="Account" title="Log In" />
      <Section>
        <div className="mx-auto max-w-md rounded-lg border border-border bg-card p-6">
          <div className="mb-5 flex gap-2 rounded-md bg-secondary p-1 text-sm font-semibold">
            <button
              onClick={() => setMode("signin")}
              className={`flex-1 rounded px-3 py-1.5 ${mode === "signin" ? "bg-background text-primary shadow" : "text-muted-foreground"}`}
            >
              Sign in
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 rounded px-3 py-1.5 ${mode === "signup" ? "bg-background text-primary shadow" : "text-muted-foreground"}`}
            >
              Create account
            </button>
          </div>

          <button
            onClick={handleGoogle}
            className="mb-4 inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted"
          >
            Continue with Google
          </button>

          <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleEmail} className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Email</label>
              <input name="email" type="email" required className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Password</label>
              <input name="password" type="password" required minLength={6} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <button
              disabled={loading}
              className="w-full rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
            </button>
          </form>
        </div>
      </Section>
    </>
  );
}
